import React from 'react';
import { useNavigate } from 'react-router-dom';

const AppPrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen bg-canvas text-text-primary py-24 px-6 md:px-12 relative">
      {/* Close Button - Top Right */}
      <nav className="fixed top-0 left-0 right-0 z-50 pointer-events-none safe-top">
        <div className="relative pointer-events-auto flex items-center justify-end h-20 px-6 md:px-12 w-full">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-300 transition-colors p-2 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-4 focus:outline-none z-10"
            aria-label="Close privacy policy"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl md:text-7xl font-light tracking-[-0.02em] mb-4 text-center">
          Privacy Policy
        </h1>
        <p className="text-sm text-text-secondary text-center opacity-60 mb-12">
          Effective Date: December 2, 2025
        </p>

        <div className="space-y-8 text-text-primary font-light leading-relaxed">
          <section>
            <p className="text-lg text-text-primary">
              This Privacy Policy explains how we collect, use, disclose, and protect the personal information you provide or that we collect when you use our mobile application (the "App"). It explains your privacy rights and choices, and how to contact us about our privacy practices. Please read this Policy carefully. By using our App, you consent to the data practices described in this Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              Entity and Contact Information
            </h2>
            <p className="text-lg text-text-primary mb-4">
              This Privacy Policy is published on behalf of our business entity operating in WY, United States.
            </p>
            <p className="text-lg text-text-primary mb-2">
              <strong className="text-text-primary">Business Name:</strong> SilentLabs LLC
            </p>
            <p className="text-lg text-text-primary mb-4">
              <strong className="text-text-primary">Registered Address:</strong> 30 N Gould St Ste 52683, Sheridan
            </p>
            <p className="text-lg text-text-primary mb-4">
              All references to "we", "us", or "our" refer to the business entity identified above.
            </p>
            <p className="text-lg text-text-primary">
              <strong className="text-text-primary">How to Contact Us:</strong> If you have questions, requests, or concerns about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              1. Information We Collect
            </h2>
            <p className="text-lg text-text-primary mb-4">
              We collect multiple types of information to operate effectively and provide you the best experience. The categories of information we collect include:
            </p>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.1. Information You Provide Directly
            </h3>
            <p className="text-lg text-text-primary mb-4">
              When you register, create an account, or use the App, you may provide:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li><strong className="text-text-primary">Name:</strong> Your first name (optional)</li>
              <li><strong className="text-text-primary">Email address:</strong> Required for account creation</li>
              <li><strong className="text-text-primary">Authentication data:</strong> When you sign in with Apple or use email authentication</li>
              <li><strong className="text-text-primary">User preferences:</strong> Your meditation experience level (Beginner, Intermediate, Advanced), onboarding goals/intents, notification preferences</li>
              <li><strong className="text-text-primary">Journal entries:</strong> Text content you write in your meditation journal</li>
              <li><strong className="text-text-primary">Pre-session check-in data:</strong> Your selected emotion, energy level, and focus level before guided meditation sessions</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.2. Automatically Collected Information
            </h3>
            <p className="text-lg text-text-primary mb-4">
              We automatically collect technical and usage data when you use the App, which may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Device identifiers, device model and operating system</li>
              <li>App usage data (features used, session duration, completion status)</li>
              <li>Performance metrics and crash logs (for app improvement)</li>
              <li>IP address (collected by our backend service provider, Supabase, for security purposes)</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.3. Meditation Session Data
            </h3>
            <p className="text-lg text-text-primary mb-4">
              When you use guided meditations or access the meditation gallery, we collect:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li><strong className="text-text-primary">Session metadata:</strong> Title, duration, start and end timestamps, completion status</li>
              <li><strong className="text-text-primary">Pre-session check-in responses:</strong> Emotion, energy level, focus level</li>
              <li><strong className="text-text-primary">Audio playback tracking:</strong> Which meditations you access and complete</li>
              <li><strong className="text-text-primary">Session history:</strong> Your meditation activity for personalization and progress tracking</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.4. Subscription Data
            </h3>
            <p className="text-lg text-text-primary mb-4">
              When you subscribe to Nura Premium through the App Store:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>We receive subscription status and metadata from Apple (via StoreKit)</li>
              <li>This includes: Product ID, transaction ID, subscription tier (monthly/annual), trial dates, subscription start/end dates, auto-renewal status</li>
              <li>We use RevenueCat to help monitor subscription lifecycle events and subscription analytics</li>
              <li><strong className="text-text-primary">We do NOT collect or process payment information</strong> (credit cards, billing addresses, payment methods)</li>
              <li>All payment processing is handled entirely by Apple through the App Store</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.5. Optional HealthKit Data
            </h3>
            <p className="text-lg text-text-primary mb-4">
              If you choose to enable Apple Health integration:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>We may save mindful minutes sessions to your Health app</li>
              <li>This includes: Start time, end time, and duration of completed meditation sessions</li>
              <li>HealthKit data is stored locally on your device and synced to your iCloud account (if enabled)</li>
              <li>We only access HealthKit data if you explicitly grant permission</li>
              <li>You can disable HealthKit integration at any time in Settings</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              1.6. Derived and Aggregated Data
            </h3>
            <p className="text-lg text-text-primary">
              We may derive non-identifying, aggregated, or de-identified data from the information we collect. Aggregated data is used for analytics and product improvement; it cannot reasonably be used to identify you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              2. How We Use Information
            </h2>
            <p className="text-lg text-text-primary mb-4">
              We use collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>To provide, operate, maintain, and improve the App and related services</li>
              <li>To authenticate users, manage accounts, and provide customer support</li>
              <li>To generate personalized guided meditation sessions based on your check-in responses</li>
              <li>To track your meditation progress and provide session history</li>
              <li>To sync your data across devices (when logged in)</li>
              <li>To process subscription status and provide premium features</li>
              <li>To comply with legal obligations and protect rights and safety</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              3. Legal Bases for Processing
            </h2>
            <p className="text-lg text-text-primary mb-4">
              To the extent applicable, our lawful bases for processing personal data may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li><strong className="text-text-primary">Consent:</strong> You have given consent for specific processing activities (e.g., HealthKit integration, notifications)</li>
              <li><strong className="text-text-primary">Contractual Necessity:</strong> Processing required to perform our agreement with you (e.g., account provisioning, subscription management)</li>
              <li><strong className="text-text-primary">Legal Obligation:</strong> Processing required to comply with law or regulatory requests</li>
              <li><strong className="text-text-primary">Legitimate Interests:</strong> Processing necessary for our legitimate interests, balanced against your rights (e.g., fraud prevention, network security, service improvement)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              4. Third-Party Services
            </h2>
            <p className="text-lg text-text-primary mb-4">
              We use the following third-party services that may process your data:
            </p>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.1. Supabase
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Our backend service provider for authentication, database storage, and data synchronization</li>
              <li>Stores: User profiles, meditation sessions, journal entries, user settings, subscription metadata</li>
              <li>Supabase's privacy practices apply to data stored on their platform</li>
              <li>Data is encrypted in transit and at rest</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.2. Sign in with Apple
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Apple processes your authentication when you sign in with Apple</li>
              <li>We receive your name and email (if you choose to share them)</li>
              <li>Apple's privacy practices apply to authentication data</li>
              <li>You can manage your Apple ID privacy settings in your Apple account</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.3. StoreKit (App Store Subscriptions)
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Apple processes all subscription payments through the App Store</li>
              <li>We only receive subscription status and transaction metadata from Apple</li>
              <li>We never see or process payment information (credit cards, billing addresses)</li>
              <li>Apple's privacy practices apply to payment processing</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.4. HealthKit (Optional)
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Apple's HealthKit framework manages health data storage</li>
              <li>Data is stored locally on your device and synced to iCloud (if enabled)</li>
              <li>We only access HealthKit data if you grant permission</li>
              <li>Apple's privacy practices apply to HealthKit data</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.5. AI Content Generation Services
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>We use AI services (such as OpenAI) to generate personalized meditation content</li>
              <li>We use text-to-speech services to convert meditation scripts to audio</li>
              <li><strong className="text-text-primary">Only non-identifying, derived data is sent to these services:</strong> Brainwave parameters and meditation strategy (no user identifiers, no email, no personal information)</li>
              <li>Your raw check-in responses (emotion, energy, focus) are converted to derived parameters by our backend before being sent to AI services</li>
              <li>AI service providers cannot link content generation requests to any specific user</li>
              <li>AI service providers' privacy practices apply to any data they receive</li>
            </ul>
            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.6. Firebase and Google Analytics for Firebase
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>We use Firebase Crashlytics and Google Analytics for Firebase to monitor app stability and performance</li>
              <li>These services collect crash reports, stack traces, and device state information when the app crashes</li>
              <li>They also collect basic usage metrics (e.g., app launches, screen views) to help us understand how the app is used and improve its performance</li>
              <li>This data is used for technical diagnostics and product improvement, not for advertising</li>
              <li>Google's privacy policy applies to data collected by these services</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
              4.7. RevenueCat Subscription Analytics
            </h3>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>We use RevenueCat to monitor subscription lifecycle events, such as trials, purchases, renewals, cancellations, refunds, and subscription status changes</li>
              <li>All billing and payment processing remains handled by Apple through the App Store</li>
              <li>RevenueCat may receive your app user identifier, subscription product information, transaction metadata, and limited device/app information needed for subscription analytics</li>
              <li>RevenueCat may collect Apple Search Ads attribution using Apple's AdServices framework to help us understand subscription performance from App Store campaigns</li>
              <li>We do not use RevenueCat to request App Tracking Transparency permission or track you across other companies' apps and websites</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              5. Advertising and Analytics
            </h2>
            <p className="text-lg text-text-primary mb-4">
              We do NOT:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Display advertisements in the App</li>
              <li>Use third-party advertising networks</li>
              <li>Sell your data to advertisers</li>
              <li>Share your data for advertising purposes</li>
            </ul>
            <p className="text-lg text-text-secondary mt-4">
              We DO use technical analytics services (Firebase Crashlytics, Google Analytics for Firebase, and RevenueCat) solely for the purpose of:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4 mt-2">
              <li>Monitoring app stability and performance</li>
              <li>Identifying and fixing crashes</li>
              <li>Understanding basic usage patterns, subscription status, and subscription lifecycle events to improve the user experience</li>
            </ul>
            <p className="text-lg text-text-secondary mt-4">
              These services are not used to build advertising profiles or track you across other apps and websites.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              6. Sharing and Disclosure
            </h2>
            <p className="text-lg text-text-primary mb-4">
              We do NOT sell your personal information. We disclose personal information only as described below or with your consent:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li><strong className="text-text-primary">To Supabase:</strong> Our backend service provider, for hosting and data storage</li>
              <li><strong className="text-text-primary">To Apple:</strong> For authentication (Sign in with Apple), subscription processing (StoreKit), and optional health data storage (HealthKit)</li>
              <li><strong className="text-text-primary">To RevenueCat:</strong> For subscription analytics, lifecycle event monitoring, and Apple Search Ads subscription attribution</li>
              <li><strong className="text-text-primary">To comply with legal processes:</strong> Such as court orders, subpoenas, or government investigations</li>
              <li><strong className="text-text-primary">To protect rights and safety:</strong> When necessary to protect our rights, property, or safety, or the rights, property, or safety of others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              7. International Data Transfers
            </h2>
            <p className="text-lg text-text-primary">
              Our operations may involve transferring personal information to countries outside your country of residence:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4 mt-4">
              <li>Supabase may store data in data centers located in various regions</li>
              <li>Apple processes authentication and subscription data globally</li>
              <li>When we transfer data internationally, we implement appropriate safeguards, such as standard contractual clauses or other mechanisms permitted by law, to protect the data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              8. Data Retention and Deletion
            </h2>
            <p className="text-lg text-text-primary">
              We retain personal information for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of your account and personal data at any time through the App's Settings → Account → Delete Account feature. We will process deletion requests in accordance with applicable law. Deletion may be subject to verification steps and exceptions where retention is required for legal or legitimate business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              9. Security Measures
            </h2>
            <p className="text-lg text-text-primary">
              We use administrative, technical, and physical controls designed to protect personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4 mt-4">
              <li>Encryption in transit and at rest (via Supabase)</li>
              <li>Secure authentication (Sign in with Apple, Supabase Auth)</li>
              <li>Row-level security policies in our database</li>
              <li>Access controls and secure data centers (via Supabase)</li>
              <li>While we strive to use reasonable safeguards, no system can be guaranteed to be 100% secure</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              10. Children's Privacy
            </h2>
            <p className="text-lg text-text-primary">
              Our App is not directed to children under the age of 13 (or the minimum age in your jurisdiction, which may be 16 in some regions). We do not knowingly collect personal information from children under the applicable age without parental consent. If a parent or guardian believes we have collected information from a child under the applicable age without consent, please contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              {' '}and we will take steps to delete such data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              11. Your Privacy Rights
            </h2>
            <p className="text-lg text-text-primary mb-4">
              Depending on where you reside, you may have rights including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li><strong className="text-text-primary">Right to access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-text-primary">Right to correct:</strong> Request correction of inaccurate data</li>
              <li><strong className="text-text-primary">Right to delete:</strong> Request deletion of your account and data</li>
              <li><strong className="text-text-primary">Right to portability:</strong> Request export of your data (available in Settings → Account → Export Data)</li>
              <li><strong className="text-text-primary">Right to restrict or object to processing</strong></li>
              <li><strong className="text-text-primary">Right to withdraw consent</strong> (e.g., disable HealthKit integration)</li>
            </ul>
            <p className="text-lg text-text-secondary mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              . We will verify requests consistent with applicable law and respond within applicable timeframes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              12. Rights for European Residents (GDPR)
            </h2>
            <p className="text-lg text-text-primary mb-4">
              If you are located in the European Economic Area (EEA), you have additional rights under the GDPR:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Right to access your personal data</li>
              <li>Right to rectification (correction)</li>
              <li>Right to erasure (deletion)</li>
              <li>Right to restriction of processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="text-lg text-text-secondary mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              13. Rights for California Residents (CCPA/CPRA)
            </h2>
            <p className="text-lg text-text-primary">
              If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-secondary ml-4 mt-4">
              <li>Right to know what personal information we collect, use, and disclose</li>
              <li>Right to delete your personal information</li>
              <li>Right to opt-out of the sale or sharing of personal information (we do not sell your data)</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p className="text-lg text-text-secondary mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              14. Third-Party Links and Integrations
            </h2>
            <p className="text-lg text-text-primary">
              The App may contain links to third-party websites or integrate with third-party services (such as Apple's App Store, Health app). We are not responsible for their privacy practices. Review their privacy policies before providing personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              15. Changes to This Policy
            </h2>
            <p className="text-lg text-text-primary">
              We may update this Privacy Policy from time to time. When we make material changes, we will provide notice in the App or by other means as required by law. The updated Policy will indicate the Effective Date. Continued use of the App after changes constitutes your acceptance of the revised Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              16. Data Breach Response
            </h2>
            <p className="text-lg text-text-primary">
              In the event of a security breach that affects personal information, we will follow our incident response plan, take reasonable steps to mitigate harm, and notify affected individuals and regulators as required by applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              17. Automated Decision-Making
            </h2>
            <p className="text-lg text-text-primary">
              We may use automated systems, including algorithms, to generate personalized meditation sessions based on your check-in responses and preferences. This is used solely to provide you with relevant meditation content. You can always skip or modify these features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              18. Account Deletion and Data Export
            </h2>
            <p className="text-lg text-text-primary mb-4">
              You can delete your account at any time through Settings → Account → Delete Account. This will:
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
              <li>Delete your account and all associated data from our servers</li>
              <li>Remove your profile, meditation sessions, journal entries, and settings</li>
              <li>Cancel any active subscriptions (managed through Apple)</li>
            </ul>
            <p className="text-lg text-text-secondary mt-4">
              You can export your data before deletion through Settings → Account → Export Data. This provides a JSON file containing your profile, settings, journal entries, and meditation session history.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              19. How to Exercise Your Rights
            </h2>
            <p className="text-lg text-text-primary">
              To exercise your rights, or to inquire about our privacy practices, please contact us at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              . We will attempt to respond within applicable statutory timelines and may require verification of identity to protect your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              20. Complaint Mechanisms
            </h2>
            <p className="text-lg text-text-primary">
              If you are in the EU/EEA, you may lodge a complaint with a data protection authority. If you are in another jurisdiction and wish to file a complaint, contact your local supervisory body or our designated contact for privacy matters at{' '}
              <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                support@nurameditation.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              21. Additional Disclosures
            </h2>
            <p className="text-lg text-text-primary">
              We may provide additional privacy disclosures in context (for example, within the App's privacy settings or account pages). Those supplemental notices are incorporated into this Policy where applicable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              22. Miscellaneous
            </h2>
            <p className="text-lg text-text-primary">
              Where any provision of this Policy conflicts with applicable law, such provision will be interpreted to the extent necessary to comply with the law. Headings are for convenience only and do not affect interpretation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
              23. Final Notes
            </h2>
            <p className="text-lg text-text-primary">
              We strive to maintain transparent and robust privacy practices. Thank you for trusting Nura with your information. Please review this Policy periodically for updates.
            </p>
          </section>

          <section className="pt-8 border-t border-white/10">
            <p className="text-sm text-text-secondary opacity-60">
              Publisher: SilentLabs LLC — 30 N Gould St Ste 52683, Sheridan.
            </p>
            <p className="text-sm text-text-secondary opacity-60 mt-2">
              Last updated: December 2, 2025
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AppPrivacyPolicy;
